import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiX, FiEdit2, FiPaperclip, FiCheck } from "react-icons/fi";
import AiChatInput from "../../AiChatComponent/panelComponents/AiChatInput";
import { MentionEditorHandle } from "../../AiChatComponent/panelComponents/MentionEditor";
import { AttachedFile } from "../../../services/mcpService";
import useAtMention, { MentionedAsset } from "../../AiChatComponent/hooks/useAtMention";
import ChatMapDrawerModal from "../ChatMapDrawerModal";
import { MODAL_SIZE_WIDTHS } from "../ModalBase";
import useScheduleQuestionInput from "./useScheduleQuestionInput";
import AntSwitch from "../../customWidget/AntSwitch";
import { Field } from "../../ReusableComponents/Select";

// Match ModalGlobal's chat map drawer (__modalSize: "2xl") so width/breakpoint follow if 2xl changes.
const MAP_DRAWER_WIDTH = parseInt(MODAL_SIZE_WIDTHS["2xl"], 10); // 1200
const MAP_DRAWER_FULLSCREEN_BELOW = MAP_DRAWER_WIDTH + 32; // go full-screen within 32px of the edges

export interface ScheduleQuestionValue {
  id: string; // runner id (pre-generated for new schedules, persisted when editing)
  title: string;
  prompt: string;
  attachments?: AttachedFile[];
  mentionedAssets?: MentionedAsset[];
  // When true, this question's scheduled answer is emailed to the schedule owner.
  sendEmail?: boolean;
  // How the email behaves: 'always' = every run; 'gate' = only when the condition is met; 'notify' = every run, but the subject flips to "Alert:" when the condition is met.
  emailMode?: 'always' | 'gate' | 'notify';
  // Natural-language condition ("the reservoir level exceeds 80%"), required for gate/notify modes. Judged against the answer after each run.
  emailCondition?: string;
}

// gate/notify need a non-empty condition; modals use this to block save.
export const isEmailConditionInvalid = (q: ScheduleQuestionValue): boolean =>
  (q.sendEmail ?? false) && (q.emailMode === 'gate' || q.emailMode === 'notify') && !q.emailCondition?.trim();

const EMAIL_MODES = [
  { key: 'always', label: 'Always' },
  { key: 'gate', label: 'Only if condition met' },
  { key: 'notify', label: 'Always + alert when met' },
] as const;

interface ScheduleQuestionEditorProps {
  value: ScheduleQuestionValue;
  scheduleId: string;
  index: number;
  onChange: (patch: Partial<ScheduleQuestionValue>) => void;
  onRemove?: () => void;
  // Notified when this question's map drawer opens/closes so the parent modal can hide itself (the schedule form stays mounted to preserve state — see the portal note below).
  onMapDrawerToggle?: (open: boolean) => void;
}

// Expand-on-click schedule question: collapsed shows a preview + @N/📎N badges; expanded reuses the chat's <AiChatInput> (attachments, map-draw, @-mentions).
const ScheduleQuestionEditor: React.FC<ScheduleQuestionEditorProps> = ({
  value,
  scheduleId,
  index,
  onChange,
  onRemove,
  onMapDrawerToggle,
}) => {
  const [expanded, setExpanded] = useState(!value.prompt.trim());
  const [showMapDrawer, setShowMapDrawer] = useState(false);

  // Track viewport width so the map drawer can go full-screen near the edges, the same way ModalGlobal sizes its 2xl chat map drawer. Only listen while the drawer is open — windowWidth is unused otherwise, so idle editors don't each hold a resize listener.
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    if (!showMapDrawer) return;
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showMapDrawer]);
  const isMapFullScreen = windowWidth < MAP_DRAWER_FULLSCREEN_BELOW;

  // Keep the parent modal in sync with the drawer's visibility so it can hide itself while the map takes over the screen. The cleanup re-shows the parent if this editor unmounts (e.g. the question is removed) while its map is open. Route through a ref so a changed callback reference can't re-run these effects and flicker the parent's isMapOpen state.
  const onMapDrawerToggleRef = useRef(onMapDrawerToggle);
  onMapDrawerToggleRef.current = onMapDrawerToggle;
  useEffect(() => {
    onMapDrawerToggleRef.current?.(showMapDrawer);
  }, [showMapDrawer]);
  useEffect(() => () => onMapDrawerToggleRef.current?.(false), []);

  // Capture Escape before the schedule modal's Dialog so it closes only the map, not the whole modal.
  useEffect(() => {
    if (!showMapDrawer) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      event.preventDefault();
      setShowMapDrawer(false);
    };
    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [showMapDrawer]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<MentionEditorHandle>(null);

  const {
    attachedFiles,
    isUploading,
    error,
    handleFileSelect,
    handleRemoveFile,
    handleMapComplete,
  } = useScheduleQuestionInput({
    scheduleId,
    runnerId: value.id,
    initialAttachments: value.attachments ?? [],
    onAttachmentsChange: (files) => onChange({ attachments: files }),
  });

  // @-mention state, per question (mirrors the chat's useAtMention wiring).
  const {
    mentionedAssets,
    isPickerOpen: isMentionPickerOpen,
    mentionQuery,
    filteredAssets: mentionSuggestions,
    handleInputChange: mentionHandleInputChange,
    registerMention,
    resolveEntity,
    closePicker,
    restoreMentions,
    resolveMentionsFromText,
  } = useAtMention();

  // Edit mode: the saved prompt carries @-mention tokens but not the structured assets, so re-derive the chips from the tokens once the asset list has loaded.
  const derivedRef = useRef(false);
  useEffect(() => {
    if (derivedRef.current) return;
    if (!value.prompt.includes("@")) {
      derivedRef.current = true;
      return;
    }
    const derived = resolveMentionsFromText(value.prompt);
    if (derived.length) {
      restoreMentions(derived);
      derivedRef.current = true;
    }
  }, [value.prompt, resolveMentionsFromText, restoreMentions]);

  // Bubble mention changes up so the modal can persist them on the runner.
  const lastSerializedRef = useRef<string>(JSON.stringify(value.mentionedAssets ?? []));
  useEffect(() => {
    const serialized = JSON.stringify(mentionedAssets);
    if (serialized !== lastSerializedRef.current) {
      lastSerializedRef.current = serialized;
      onChange({ mentionedAssets });
    }
    // onChange identity is stable enough; intentionally keyed on the mentions only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionedAssets]);

  const handleEditorChange = (next: string, caretOffset: number) => {
    mentionHandleInputChange(next, { selectionStart: caretOffset });
    onChange({ prompt: next });
  };

  const handleMentionSelect = (asset: MentionedAsset) => {
    registerMention(asset);
    editorRef.current?.insertMention(asset);
  };

  const attachmentCount = attachedFiles.length;

  return (
    // Each task is its own card — on a phone the old Q-gutter + trailing remove button squeezed the middle column to nothing.
    <div className="eai-card flex flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <span className="eai-meta-label">Task {index + 1}</span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="eai-btn-icon eai-btn-icon-danger ml-auto"
            title="Remove task"
            aria-label="Remove task"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Title is always editable */}
      <input
        value={value.title}
        onChange={(e) => onChange({ title: e.target.value })}
        className="eai-input"
        placeholder="Title (e.g. Market Summary)"
      />

      {expanded ? (
        <div className="flex flex-col gap-1">
          <AiChatInput
            inputRef={inputRef}
            inputValue={value.prompt}
            onInputChange={(next) => onChange({ prompt: next })}
            onKeyDown={() => {}}
            onSend={() => {}}
            hideSend
            isLoading={false}
            error={error}
            variant="analyst"
            placeholder="Prompt — type @ to reference an asset"
            containerClassName=""
            textareaSizeClassName="min-h-[56px] max-h-40 sm:min-h-[80px] sm:max-h-60"
            attachedFiles={attachedFiles}
            isUploading={isUploading}
            onFileSelect={handleFileSelect}
            onRemoveFile={handleRemoveFile}
            onMapDraw={() => setShowMapDrawer(true)}
            mentionedAssets={mentionedAssets}
            isMentionPickerOpen={isMentionPickerOpen}
            mentionQuery={mentionQuery}
            mentionSuggestions={mentionSuggestions}
            onMentionSelect={handleMentionSelect}
            onMentionPickerClose={closePicker}
            editorRef={editorRef}
            onEditorChange={handleEditorChange}
            resolveMentionEntity={resolveEntity}
          />
          <button type="button" onClick={() => setExpanded(false)} className="eai-btn-outline-xs self-end">
            <FiCheck className="h-3 w-3" /> Done
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="group flex items-start gap-2 rounded-eai-card border border-eai-border bg-eai-surface-control px-3 py-2 text-left transition-colors hover:border-eai-border-hover hover:bg-eai-surface-hover active:bg-eai-surface-active"
        >
          <span className="line-clamp-2 flex-1 font-inter text-eai-body-sm text-eai-ink-muted">
            {value.prompt.trim() || (
              <span className="text-eai-ink-disabled">Click to add a prompt…</span>
            )}
          </span>
          {mentionedAssets.length > 0 && (
            <span className="mt-0.5 flex-none font-inter text-eai-body-xs text-eai-ink-subtle">
              @{mentionedAssets.length}
            </span>
          )}
          {attachmentCount > 0 && (
            <span className="mt-0.5 flex flex-none items-center gap-1 font-inter text-eai-body-xs text-eai-ink-subtle">
              <FiPaperclip className="h-3 w-3" />
              {attachmentCount}
            </span>
          )}
          <FiEdit2 className="mt-0.5 h-3.5 w-3.5 flex-none text-eai-ink-faint transition-colors group-hover:text-eai-ink-soft" />
        </button>
      )}

      {/* Email the answer for this question to the schedule owner when it runs. */}
      <label className="mt-0.5 flex cursor-pointer select-none items-center gap-2 font-inter text-eai-body-sm text-eai-ink-muted">
        <AntSwitch
          checked={value.sendEmail ?? false}
          onChange={(e) => onChange({ sendEmail: e.target.checked })}
        />
        Email me this answer
      </label>

      {/* Email mode + condition. State is kept (not cleared) when the switch toggles off — the executor only forwards the condition while sendEmail is on. */}
      {(value.sendEmail ?? false) && (
        <>
          {/* Chips, not a segmented track — the longest label doesn't fit one row on a phone */}
          <div className="flex flex-wrap gap-2">
            {EMAIL_MODES.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => onChange({ emailMode: key })}
                className={`eai-chip ${(value.emailMode ?? 'always') === key ? 'eai-chip-active' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
          {(value.emailMode === 'gate' || value.emailMode === 'notify') && (
            <Field
              error={isEmailConditionInvalid(value) ? 'Enter a condition, or switch to Always.' : undefined}
              hint="Checked against this task’s answer — make sure the question asks for the data your condition needs."
            >
              <textarea
                value={value.emailCondition ?? ''}
                onChange={(e) => onChange({ emailCondition: e.target.value })}
                rows={2}
                maxLength={500}
                placeholder={value.emailMode === 'gate'
                  ? 'Only email me if… (e.g. "the reservoir level exceeds 80%")'
                  : 'Alert me when… (e.g. "the reservoir level exceeds 80%")'}
                className={`eai-input resize-none ${
                  isEmailConditionInvalid(value)
                    ? 'border-eai-danger-border-strong focus:border-eai-danger-border-strong focus:ring-eai-danger-focus'
                    : ''
                }`}
              />
            </Field>
          )}
        </>
      )}

      {/* Map drawer — portaled to <body> so it escapes the schedule modal's DOM subtree. That lets the parent hide the schedule form (via onMapDrawerToggle) while keeping it mounted, so the map takes over the screen on its own and no entered state is lost. z sits above ModalGlobal's z-[9999] layer. Reuses ChatMapDrawerModal for chat parity; this wrapper replicates ModalGlobal's panel: 2xl width and full-screen when the viewport gets within 32px (both derived from the MODAL_SIZE_WIDTHS["2xl"] token above). */}
      {showMapDrawer &&
        createPortal(
          <div className={`fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 ${isMapFullScreen ? "p-0" : "p-4"}`}>
            <div
              className={`w-full overflow-hidden bg-white shadow-xl ${isMapFullScreen ? "max-w-full rounded-none" : "rounded-lg"}`}
              style={{ maxWidth: isMapFullScreen ? undefined : MAP_DRAWER_WIDTH }}
            >
              <ChatMapDrawerModal
                onClose={(result) => {
                  if (result?.type === "chatMapDraw") {
                    handleMapComplete(result.kmlContent, result.filename);
                  }
                  setShowMapDrawer(false);
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ScheduleQuestionEditor;
